/**
 * Admin — Estrutura de custos (versões imutáveis para validação de preço Rent_Bud).
 * Cada gravação cria uma nova versão e fica activa por defeito (API).
 */
import { useCallback, useEffect, useState } from 'react';
import { Save, Plus, Trash2 } from 'lucide-react';
import { configAPI } from '@/services/api';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

let lineKey = 0;
const nextKey = () => {
  lineKey += 1;
  return `L${lineKey}`;
};

/**
 * Input decimal com estado de texto local: mantém entradas parciais como
 * "0." ou "0," enquanto o utilizador escreve (um input controlado
 * directamente pelo número reformatava a cada tecla e "engolia" o ponto).
 * Aceita ponto ou vírgula como separador decimal.
 */
function DecimalInput({ value, onChange, nullable = true, ...props }) {
  const emptyValue = nullable ? null : 0;
  const [text, setText] = useState(
    value === null || value === undefined || Number.isNaN(value) ? '' : String(value)
  );
  // Re-sincroniza o texto quando o valor muda por fora (load, save, reset),
  // mas nunca enquanto o texto actual já representa o mesmo número.
  useEffect(() => {
    const parsed = parseFloat(String(text).replace(',', '.'));
    const current =
      text === '' || text === '-'
        ? emptyValue
        : Number.isFinite(parsed)
          ? parsed
          : null;
    const incoming =
      value === null || value === undefined || Number.isNaN(value) ? emptyValue : value;
    if (incoming !== current) {
      setText(incoming === null ? '' : String(incoming));
    }
  }, [value, text, emptyValue]);
  const handle = (e) => {
    const raw = e.target.value;
    setText(raw);
    if (raw === '' || raw === '-') {
      onChange(emptyValue);
      return;
    }
    const num = parseFloat(raw.replace(',', '.'));
    if (Number.isFinite(num)) onChange(num);
  };
  return <Input type="text" inputMode="decimal" value={text} onChange={handle} {...props} />;
}

function NumberField({ id, label, value, onChange, step = 1, hint }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <DecimalInput id={id} className="tnum" value={value} onChange={onChange} step={step} />
      {hint ? <p className="text-[11px] leading-snug text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function versionToDraft(v) {
  if (!v) return null;
  // Compat com snapshots antigos: ``shifts_per_day`` (campo unificado)
  // alimenta os dois novos campos quando o backend ainda não enviou os
  // distintos. Após a primeira gravação, a versão guardada já tem ambos.
  const legacyShifts = v.shifts_per_day;
  return {
    name: '',
    working_days_per_year: v.working_days_per_year,
    shifts_per_day_injection:
      v.shifts_per_day_injection ?? legacyShifts ?? 1,
    shifts_per_day_finishing:
      v.shifts_per_day_finishing ?? legacyShifts ?? 1,
    hours_per_turn_injection: v.hours_per_turn_injection,
    hours_per_turn_finishing: v.hours_per_turn_finishing,
    injection_machine_count: v.injection_machine_count,
    capacity_utilization_rate: v.capacity_utilization_rate,
    macro_scrap_rate: v.macro_scrap_rate,
    finishing_staff_cost_eur_per_fte_year: v.finishing_staff_cost_eur_per_fte_year,
    fire_loss_resale_pct: v.fire_loss_resale_pct ?? 0,
    slag_valuation_rate: v.slag_valuation_rate ?? 0,
    slag_share_rate: v.slag_share_rate ?? 0,
    chips_valuation_rate: v.chips_valuation_rate ?? 0,
    chips_share_rate: v.chips_share_rate ?? 0,
    margin_pct_green_threshold: v.margin_pct_green_threshold,
    margin_pct_warn_threshold: v.margin_pct_warn_threshold,
    fixed_lines: (v.fixed_lines || []).map((fl, i) => ({
      _k: nextKey(),
      label: fl.label || '',
      annual_amount_eur: fl.annual_amount_eur ?? 0,
      sort_order: fl.sort_order ?? i,
    })),
    variable_pct_lines: (v.variable_pct_lines || []).map((vl, i) => ({
      _k: nextKey(),
      label: vl.label || '',
      pct_of_va: vl.pct_of_va ?? 0,
      sort_order: vl.sort_order ?? i,
    })),
  };
}

export default function FactoryPnLConfigPage({ lang }) {
  const [activeMeta, setActiveMeta] = useState({ id: null, name: '', is_active: false });
  const [draft, setDraft] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRes, listRes] = await Promise.all([
        configAPI.getCostStructureActive(),
        configAPI.listCostStructureVersions(),
      ]);
      const active = activeRes.data;
      setActiveMeta({ id: active.id, name: active.name || '', is_active: !!active.is_active });
      setDraft(versionToDraft(active));
      setVersions(listRes.data || []);
    } catch (err) {
      toast.error(t('cs_load_error', lang));
      setDraft(null);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    load();
  }, [load]);

  const updateScalar = (field, value) => {
    setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const addFixedLine = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      const n = prev.fixed_lines.length;
      return {
        ...prev,
        fixed_lines: [
          ...prev.fixed_lines,
          { _k: nextKey(), label: '', annual_amount_eur: 0, sort_order: n },
        ],
      };
    });
  };

  const removeFixedLine = (k) => {
    setDraft((prev) =>
      prev ? { ...prev, fixed_lines: prev.fixed_lines.filter((x) => x._k !== k) } : prev
    );
  };

  const updateFixedLine = (k, field, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        fixed_lines: prev.fixed_lines.map((row) =>
          row._k === k ? { ...row, [field]: value } : row
        ),
      };
    });
  };

  const addVarLine = () => {
    setDraft((prev) => {
      if (!prev) return prev;
      const n = prev.variable_pct_lines.length;
      return {
        ...prev,
        variable_pct_lines: [
          ...prev.variable_pct_lines,
          { _k: nextKey(), label: '', pct_of_va: 0, sort_order: n },
        ],
      };
    });
  };

  const removeVarLine = (k) => {
    setDraft((prev) =>
      prev
        ? { ...prev, variable_pct_lines: prev.variable_pct_lines.filter((x) => x._k !== k) }
        : prev
    );
  };

  const updateVarLine = (k, field, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        variable_pct_lines: prev.variable_pct_lines.map((row) =>
          row._k === k ? { ...row, [field]: value } : row
        ),
      };
    });
  };

  const saveNewVersion = async () => {
    if (!draft) return;
    const name = (draft.name || '').trim();
    if (!name) {
      toast.error(t('cs_name_required', lang));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        working_days_per_year: Math.round(draft.working_days_per_year ?? 227),
        shifts_per_day_injection: Number(draft.shifts_per_day_injection ?? 1),
        shifts_per_day_finishing: Number(draft.shifts_per_day_finishing ?? 1),
        hours_per_turn_injection: Number(draft.hours_per_turn_injection ?? 8),
        hours_per_turn_finishing: Number(draft.hours_per_turn_finishing ?? 8),
        injection_machine_count: Math.round(draft.injection_machine_count ?? 1),
        capacity_utilization_rate: Number(draft.capacity_utilization_rate ?? 0.85),
        macro_scrap_rate: Number(draft.macro_scrap_rate ?? 0),
        finishing_staff_cost_eur_per_fte_year: Number(
          draft.finishing_staff_cost_eur_per_fte_year ?? 50000
        ),
        fire_loss_resale_pct: Number(draft.fire_loss_resale_pct ?? 0),
        slag_valuation_rate: Number(draft.slag_valuation_rate ?? 0),
        slag_share_rate: Number(draft.slag_share_rate ?? 0),
        chips_valuation_rate: Number(draft.chips_valuation_rate ?? 0),
        chips_share_rate: Number(draft.chips_share_rate ?? 0),
        margin_pct_green_threshold: Number(draft.margin_pct_green_threshold ?? 0.15),
        margin_pct_warn_threshold: Number(draft.margin_pct_warn_threshold ?? 0.08),
        fixed_lines: draft.fixed_lines.map((row, i) => ({
          label: row.label || '',
          annual_amount_eur: Number(row.annual_amount_eur) || 0,
          sort_order: i,
        })),
        variable_pct_lines: draft.variable_pct_lines.map((row, i) => ({
          label: row.label || '',
          pct_of_va: Number(row.pct_of_va) || 0,
          sort_order: i,
        })),
      };
      const res = await configAPI.createCostStructureVersion(payload);
      setActiveMeta({ id: res.data.id, name: res.data.name || '', is_active: true });
      setDraft(versionToDraft(res.data));
      const listRes = await configAPI.listCostStructureVersions();
      setVersions(listRes.data || []);
      toast.success(t('cs_saved', lang));
    } catch (err) {
      toast.error(formatApiError(err, t('cs_save_error', lang)));
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    try {
      const res = await configAPI.activateCostStructureVersion(id);
      setActiveMeta({ id: res.data.id, name: res.data.name || '', is_active: true });
      setDraft(versionToDraft(res.data));
      const listRes = await configAPI.listCostStructureVersions();
      setVersions(listRes.data || []);
      toast.success(t('cs_activate_done', lang));
    } catch (err) {
      toast.error(formatApiError(err, t('cs_save_error', lang)));
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-40" />
      </div>
    );
  }
  if (!draft) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <h2 className="text-base font-semibold tracking-tight">{t('pnl_admin_title', lang)}</h2>
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">{t('pnl_admin_subtitle', lang)}</p>
          {activeMeta.id != null ? (
            <p className="text-xs text-muted-foreground">
              ID {activeMeta.id}
              {activeMeta.name ? ` — ${activeMeta.name}` : ''}
            </p>
          ) : null}
        </div>
        <Button onClick={saveNewVersion} disabled={saving}>
          <Save className="h-4 w-4" /> {saving ? t('saving', lang) : t('cs_save_new_version', lang)}
        </Button>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">{t('cs_block_capacity', lang)}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="cs-name">{t('cs_name_version', lang)}</Label>
                <Input
                  id="cs-name"
                  value={draft.name}
                  onChange={(e) => updateScalar('name', e.target.value)}
                  placeholder="ex. 2026-Q1"
                />
              </div>
              <NumberField
                id="cs-wd"
                label={t('cs_working_days', lang)}
                value={draft.working_days_per_year}
                onChange={(v) => updateScalar('working_days_per_year', v ?? 227)}
              />
              <NumberField
                id="cs-shifts-inj"
                label={t('cs_shifts_injection', lang)}
                value={draft.shifts_per_day_injection}
                onChange={(v) => updateScalar('shifts_per_day_injection', v ?? 1)}
              />
              <NumberField
                id="cs-shifts-fin"
                label={t('cs_shifts_finishing', lang)}
                value={draft.shifts_per_day_finishing}
                onChange={(v) => updateScalar('shifts_per_day_finishing', v ?? 1)}
              />
              <NumberField
                id="cs-hinj"
                label={t('cs_hours_inj', lang)}
                value={draft.hours_per_turn_injection}
                onChange={(v) => updateScalar('hours_per_turn_injection', v ?? 8)}
              />
              <NumberField
                id="cs-hfin"
                label={t('cs_hours_fin', lang)}
                value={draft.hours_per_turn_finishing}
                onChange={(v) => updateScalar('hours_per_turn_finishing', v ?? 8)}
              />
              <NumberField
                id="cs-nmach"
                label={t('cs_machine_count', lang)}
                value={draft.injection_machine_count}
                onChange={(v) => updateScalar('injection_machine_count', v ?? 1)}
              />
              <NumberField
                id="cs-util"
                label={t('cs_utilization', lang)}
                value={draft.capacity_utilization_rate}
                onChange={(v) => updateScalar('capacity_utilization_rate', v ?? 0.85)}
              />
              <NumberField
                id="cs-scrap"
                label={t('cs_macro_scrap', lang)}
                value={draft.macro_scrap_rate}
                onChange={(v) => updateScalar('macro_scrap_rate', v ?? 0)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-base">{t('cs_block_fixed', lang)}</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addFixedLine}>
                <Plus className="h-4 w-4 mr-1" /> {t('cs_add_fixed_line', lang)}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('cs_fixed_label', lang)}</TableHead>
                    <TableHead className="w-[180px]">{t('cs_fixed_amount', lang)}</TableHead>
                    <TableHead className="w-[48px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draft.fixed_lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-sm text-muted-foreground py-4 text-center">
                        —
                      </TableCell>
                    </TableRow>
                  ) : (
                    draft.fixed_lines.map((row) => (
                      <TableRow key={row._k}>
                        <TableCell>
                          <Input
                            value={row.label}
                            onChange={(e) => updateFixedLine(row._k, 'label', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <DecimalInput
                            className="tabular-nums"
                            nullable={false}
                            value={row.annual_amount_eur}
                            onChange={(n) => updateFixedLine(row._k, 'annual_amount_eur', n)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeFixedLine(row._k)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">{t('cs_block_mo', lang)}</CardTitle>
            </CardHeader>
            <CardContent>
              <NumberField
                id="cs-ftecost"
                label={t('cs_finishing_fte_cost', lang)}
                value={draft.finishing_staff_cost_eur_per_fte_year}
                onChange={(v) => updateScalar('finishing_staff_cost_eur_per_fte_year', v ?? 50000)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between py-3">
              <CardTitle className="text-base">{t('cs_block_variable', lang)}</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addVarLine}>
                <Plus className="h-4 w-4 mr-1" /> {t('cs_add_var_line', lang)}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('cs_var_label', lang)}</TableHead>
                    <TableHead className="w-[160px]">{t('cs_var_pct', lang)}</TableHead>
                    <TableHead className="w-[48px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {draft.variable_pct_lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-sm text-muted-foreground py-4 text-center">
                        —
                      </TableCell>
                    </TableRow>
                  ) : (
                    draft.variable_pct_lines.map((row) => (
                      <TableRow key={row._k}>
                        <TableCell>
                          <Input
                            value={row.label}
                            onChange={(e) => updateVarLine(row._k, 'label', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <DecimalInput
                            className="tabular-nums"
                            nullable={false}
                            value={row.pct_of_va}
                            onChange={(n) => updateVarLine(row._k, 'pct_of_va', n)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeVarLine(row._k)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">{t('cs_block_fire_loss', lang)}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2">
              <NumberField
                id="cs-fireloss-resale"
                label={t('cs_fire_loss_resale_pct', lang)}
                value={draft.fire_loss_resale_pct}
                onChange={(v) => updateScalar('fire_loss_resale_pct', v ?? 0)}
              />
              <div className="hidden md:block" />
              <NumberField
                id="cs-slag-valuation"
                label={t('cs_slag_valuation_rate', lang)}
                value={draft.slag_valuation_rate}
                onChange={(v) => updateScalar('slag_valuation_rate', v ?? 0)}
              />
              <NumberField
                id="cs-slag-share"
                label={t('cs_slag_share_rate', lang)}
                hint={t('cs_slag_share_rate_hint', lang)}
                value={draft.slag_share_rate}
                onChange={(v) => updateScalar('slag_share_rate', v ?? 0)}
              />
              <NumberField
                id="cs-chips-valuation"
                label={t('cs_chips_valuation_rate', lang)}
                value={draft.chips_valuation_rate}
                onChange={(v) => updateScalar('chips_valuation_rate', v ?? 0)}
              />
              <NumberField
                id="cs-chips-share"
                label={t('cs_chips_share_rate', lang)}
                hint={t('cs_chips_share_rate_hint', lang)}
                value={draft.chips_share_rate}
                onChange={(v) => updateScalar('chips_share_rate', v ?? 0)}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">{t('cs_block_thresholds', lang)}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2">
              <NumberField
                id="cs-mg"
                label={t('cs_margin_green', lang)}
                value={draft.margin_pct_green_threshold}
                onChange={(v) => updateScalar('margin_pct_green_threshold', v ?? 0.15)}
              />
              <NumberField
                id="cs-mw"
                label={t('cs_margin_warn', lang)}
                value={draft.margin_pct_warn_threshold}
                onChange={(v) => updateScalar('margin_pct_warn_threshold', v ?? 0.08)}
              />
            </CardContent>
          </Card>
        </div>

        <Card className="h-fit bg-muted/30 shadow-none">
          <CardHeader className="py-3">
            <CardTitle className="text-base">{t('cs_versions_title', lang)}</CardTitle>
          </CardHeader>
          <CardContent className="max-h-[70vh] overflow-y-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[52px]">ID</TableHead>
                  <TableHead>{t('name', lang)}</TableHead>
                  <TableHead className="w-[88px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {versions.map((v) => (
                  <TableRow key={v.id} className={v.is_active ? 'bg-muted/40' : ''}>
                    <TableCell className="tabular-nums text-xs">{v.id}</TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-wrap items-center gap-1">
                        {v.name || '—'}
                        {v.is_active ? (
                          <Badge variant="success" className="text-[10px]">
                            {t('pnl_active', lang)}
                          </Badge>
                        ) : null}
                      </div>
                      {v.created_at ? (
                        <div className="text-[10px] text-muted-foreground">
                          {new Date(v.created_at).toLocaleString(lang === 'pt' ? 'pt-PT' : lang === 'fr' ? 'fr-FR' : 'en-GB')}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {!v.is_active ? (
                        <Button type="button" variant="outline" size="sm" onClick={() => activate(v.id)}>
                          {t('cs_activate', lang)}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
